import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Globe, Sun, Moon, User, LogOut, Heart, MessageSquare, LayoutGrid } from 'lucide-react';
import { useTranslation } from '../i18n';
import { useTheme } from '../contexts/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import NotificationBell from './NotificationBell';

const Navbar = ({ user, onLogout, lang, onChangeLang }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useTranslation(lang);
  const { theme, toggleTheme } = useTheme();

  const isActive = (path) => location.pathname === path;
  const isHome = location.pathname === '/';
  // Cinematic mode: transparent + light text over dark hero, only on top of home
  const cinematic = isHome && !scrolled;

  useEffect(() => {
    if (!isHome) return undefined;
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  const navBg = cinematic
    ? 'bg-transparent border-transparent'
    : 'glass-effect border-b';

  const linkColor = cinematic
    ? 'text-[color:var(--text-primary)]/85 hover:text-[color:var(--text-primary)]'
    : 'text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300';

  const activeLink = cinematic
    ? 'text-[color:var(--text-primary)] font-semibold'
    : 'text-black dark:text-white font-semibold';

  return (
    <nav
      className={`fixed w-full top-0 z-[70] transition-colors duration-500 ${navBg}`}
      style={{ borderColor: cinematic ? 'transparent' : 'var(--border-color)' }}
      data-testid="site-navbar"
      data-cinematic={cinematic ? 'true' : 'false'}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="text-2xl font-bold">
              <span style={{ fontFamily: 'Playfair Display', color: '#DC2626' }}>AAA</span>
              <span
                className={cinematic ? '' : 'dark:[text-shadow:_-0.5px_-0.5px_0_#fff,_0.5px_-0.5px_0_#fff,_-0.5px_0.5px_0_#fff,_0.5px_0.5px_0_#fff]'}
                style={{ fontFamily: 'Playfair Display', color: cinematic ? 'var(--text-primary)' : '#1a1a1a' }}
              > OF MIAMI</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              data-testid="nav-home"
              className={`text-base font-medium transition-colors ${
                isActive('/') ? activeLink : linkColor
              }`}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/inventory"
              data-testid="nav-inventory"
              className={`text-base font-medium transition-colors ${
                isActive('/inventory') ? activeLink : linkColor
              }`}
            >
              {t('nav.inventory')}
            </Link>
            <Link
              to="/contact"
              data-testid="nav-contact"
              className={`text-base font-medium transition-colors ${
                isActive('/contact') ? activeLink : linkColor
              }`}
            >
              {t('nav.contact')}
            </Link>
            {user?.is_admin && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className={`text-base font-medium transition-colors ${
                  location.pathname.startsWith('/admin') ? activeLink : linkColor
                }`}
              >
                {t('nav.admin')}
              </Link>
            )}
          </div>

          {/* Right side - Theme, Language, Notifications & Auth */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="w-9 h-9 p-0"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5" style={{ color: 'var(--accent-gold)' }} />
              ) : (
                <Moon className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
              )}
            </Button>

            {/* Notification Bell (only for logged in users) */}
            {user && <NotificationBell />}

            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center space-x-1" data-testid="language-selector">
                  <Globe className="w-4 h-4" />
                  <span className="text-sm uppercase font-medium">{lang}</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {languages.map((language) => (
                  <DropdownMenuItem
                    key={language.code}
                    onClick={() => onChangeLang(language.code)}
                    className={`${lang === language.code ? 'font-semibold' : ''} flex items-center space-x-2`}
                    data-testid={`lang-${language.code}`}
                  >
                    <span className="text-lg">{language.flag}</span>
                    <span>{language.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center space-x-2" data-testid="user-menu">
                    <User className="w-4 h-4" />
                    <span>{user.username}</span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="px-2 py-1.5 text-sm font-medium">
                    {user.email}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/favorites" className="flex items-center cursor-pointer">
                      <Heart className="w-4 h-4 mr-2" />
                      {t('nav.favorites')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/my-messages" className="flex items-center cursor-pointer">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      {t('messages.myMessages')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/compare" className="flex items-center cursor-pointer">
                      <LayoutGrid className="w-4 h-4 mr-2" />
                      {t('nav.compare') || 'Compare'}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center cursor-pointer">
                      <User className="w-4 h-4 mr-2" />
                      {t('profile.myProfile')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onLogout} className="text-red-600 cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('profile.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button
                  variant="default"
                  size="sm"
                  data-testid="login-btn"
                  className="bg-gray-900 text-white hover:bg-gray-800"
                >
                  {t('nav.login')}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-md text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div
            className="md:hidden py-4 border-t relative z-[60]"
            style={{
              background: 'var(--bg-primary)',
              borderColor: 'var(--border-color)',
              boxShadow: '0 10px 30px -10px var(--shadow-lg)'
            }}
            data-testid="mobile-menu"
          >
            <div className="flex flex-col space-y-4">
              <Link
                to="/"
                onClick={() => setIsOpen(false)}
                className="text-base font-medium text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300"
                data-testid="mobile-nav-home"
              >
                {t('nav.home')}
              </Link>
              <Link
                to="/inventory"
                onClick={() => setIsOpen(false)}
                className="text-base font-medium text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300"
                data-testid="mobile-nav-inventory"
              >
                {t('nav.inventory')}
              </Link>
              <Link
                to="/contact"
                onClick={() => setIsOpen(false)}
                className="text-base font-medium text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300"
                data-testid="mobile-nav-contact"
              >
                {t('nav.contact')}
              </Link>
              <Link
                to="/compare"
                onClick={() => setIsOpen(false)}
                className="text-base font-medium text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300"
                data-testid="mobile-nav-compare"
              >
                {t('nav.compare') || 'Compare'}
              </Link>
              {user && (
                <>
                  <Link
                    to="/favorites"
                    onClick={() => setIsOpen(false)}
                    className="text-base font-medium text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300"
                    data-testid="mobile-nav-favorites"
                  >
                    {t('nav.favorites')}
                  </Link>
                  <Link
                    to="/my-messages"
                    onClick={() => setIsOpen(false)}
                    className="text-base font-medium text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300"
                    data-testid="mobile-nav-my-messages"
                  >
                    {t('messages.myMessages')}
                  </Link>
                  <Link
                    to="/profile"
                    onClick={() => setIsOpen(false)}
                    className="text-base font-medium text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300"
                    data-testid="mobile-nav-profile"
                  >
                    {t('profile.myProfile')}
                  </Link>
                </>
              )}
              {user?.is_admin && (
                <Link
                  to="/admin"
                  onClick={() => setIsOpen(false)}
                  className="text-base font-medium text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300"
                  data-testid="mobile-nav-admin"
                >
                  {t('nav.admin')}
                </Link>
              )}

              {/* Notification Bell in Mobile Menu */}
              {user && (
                <div className="pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium text-black dark:text-white">
                      {t('notifications.title') || 'Notifications'}
                    </span>
                    <NotificationBell />
                  </div>
                </div>
              )}
              
              {/* Theme & Language selector mobile */}
              <div className="flex flex-col space-y-3 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                {/* Theme Toggle Mobile */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    {theme === 'dark' ? t('nav.lightMode') || 'Light Mode' : t('nav.darkMode') || 'Dark Mode'}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleTheme}
                    className="w-10 h-10 p-0"
                    data-testid="mobile-theme-toggle"
                  >
                    {theme === 'dark' ? (
                      <Sun className="w-6 h-6" style={{ color: 'var(--accent-gold)' }} />
                    ) : (
                      <Moon className="w-6 h-6" style={{ color: 'var(--text-secondary)' }} />
                    )}
                  </Button>
                </div>

                {/* Language Selector Mobile */}
                <div>
                  <span className="text-sm font-medium mb-2 block" style={{ color: 'var(--text-primary)' }}>
                    {t('nav.language') || 'Language'}
                  </span>
                  <div className="flex space-x-2">
                    {languages.map((language) => (
                      <button
                        key={language.code}
                        onClick={() => {
                          onChangeLang(language.code);
                          setIsOpen(false);
                        }}
                        className={`px-3 py-2 rounded text-sm flex items-center space-x-1 ${
                          lang === language.code
                            ? 'bg-gray-900 text-white'
                            : 'text-gray-700 hover:bg-gray-300'
                        }`}
                        style={{
                          background: lang === language.code ? 'var(--text-primary)' : 'var(--bg-tertiary)',
                          color: lang === language.code ? 'var(--bg-primary)' : 'var(--text-primary)'
                        }}
                        data-testid={`mobile-lang-${language.code}`}
                      >
                        <span>{language.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {user ? (
                <Button
                  onClick={() => {
                    onLogout();
                    setIsOpen(false);
                  }}
                  variant="outline"
                  className="w-full border-black dark:border-white text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                  data-testid="mobile-logout-btn"
                >
                  {t('nav.logout')}
                </Button>
              ) : (
                <Link to="/login" onClick={() => setIsOpen(false)}>
                  <Button
                    variant="default"
                    className="w-full bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200"
                    data-testid="mobile-login-btn"
                  >
                    {t('nav.login')}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
